/**
 * Find URLs in plain text.
 *
 * Used by the linkifier, which runs on note bodies and ticket updates — text
 * people typed. Two behaviours that look like nitpicks and are not:
 *
 *  - Trailing punctuation is excluded, so "see https://x.com/a." links to the
 *    page and not to a 404 with a dot on the end.
 *  - Balanced trailing parens are kept but an unmatched one is dropped, because
 *    "(see https://en.wikipedia.org/wiki/Foo_(bar))" is the common case and
 *    both halves of that sentence matter.
 *
 * Pure, dependency-free and safe to run in a browser bundle — the linkifier
 * needs it on the client and the resolver needs it on the server.
 */

/**
 * Scheme-bearing URLs, plus bare `www.` hosts.
 *
 * The `www.` arm is not cosmetic. People paste bare hosts constantly, and Hub
 * had a second linkifier (`components/shared/LinkifiedText.tsx`) that existed
 * largely BECAUSE this pattern was https-only — consolidating onto one
 * implementation meant the survivor had to cover both, or CRM table cells would
 * have silently stopped linking `www.acme.com` on the day the copy was deleted.
 *
 * A bare host is matched only with the `www.` prefix, deliberately. Linkifying
 * anything that merely looks domain-shaped turns "priced at 9.99" and every
 * sentence ending "...done.Next" into links; requiring `www.` keeps the rule to
 * a form people only type when they mean a URL.
 */
const URL_PATTERN = /\b(?:https?:\/\/|www\.)[^\s<>"']+/gi;
const TRAILING_PUNCTUATION = /[.,!?;:'"]+$/;

export interface TextSegment {
  kind: "text" | "url";
  /** The text as it appeared, and what a linkifier displays. */
  value: string;
  /**
   * Where the link points. Equal to `value` for scheme-bearing URLs; for a bare
   * `www.` host it carries the `https://` the user did not type.
   *
   * Separate from `value` on purpose: rendering the href as the link TEXT would
   * silently rewrite what the author wrote, and using `value` as the href would
   * produce a relative link for every bare host. Present only on url segments.
   */
  href?: string;
}

/** The href for a matched candidate: bare `www.` hosts get an explicit scheme. */
function hrefFor(url: string): string {
  return /^www\./i.test(url) ? `https://${url}` : url;
}

/** Drop trailing punctuation and any unbalanced closing bracket. */
export function trimUrlTail(candidate: string): string {
  let url = candidate.replace(TRAILING_PUNCTUATION, "");
  for (const [open, close] of [["(", ")"], ["[", "]"]] as const) {
    while (url.endsWith(close)) {
      const opens = url.split(open).length - 1;
      const closes = url.split(close).length - 1;
      if (closes <= opens) break;
      url = url.slice(0, -1);
    }
  }
  return url.replace(TRAILING_PUNCTUATION, "");
}

/**
 * Split text into alternating literal and URL segments, in order. Returns a
 * single text segment when there are no URLs, so callers have one code path.
 */
export function splitOnUrls(text: string): TextSegment[] {
  const segments: TextSegment[] = [];
  let cursor = 0;
  URL_PATTERN.lastIndex = 0;
  let match = URL_PATTERN.exec(text);

  while (match !== null) {
    const raw = match[0];
    const url = trimUrlTail(raw);
    if (url !== "") {
      if (match.index > cursor) segments.push({ kind: "text", value: text.slice(cursor, match.index) });
      segments.push({ kind: "url", value: url, href: hrefFor(url) });
      cursor = match.index + url.length;
    }
    match = URL_PATTERN.exec(text);
  }

  if (cursor < text.length) segments.push({ kind: "text", value: text.slice(cursor) });
  return segments.length === 0 ? [{ kind: "text", value: text }] : segments;
}

/**
 * Unique URLs in `text`, in first-seen order, as ABSOLUTE urls.
 *
 * Returns the href rather than the literal text: this feeds the resolver and
 * the guard, and a bare `www.acme.com` is not something either can fetch.
 */
export function findUrls(text: string): string[] {
  const seen = new Set<string>();
  for (const segment of splitOnUrls(text)) {
    if (segment.kind === "url") seen.add(segment.href ?? segment.value);
  }
  return [...seen];
}
